<script>
import axios from '@/utils/axios';
import { isAuthenticated, authUser } from '@/utils/auth';

export default {
 data() {
  return {
   isOpen: false,
   userInput: '',
   sending: false,
   messages: [],
   loadedKey: null,
   showBubble: false,
   bubbleInterval: null
  };
 },
 computed: {
  showWidget(){
   return isAuthenticated();
  }
 },
 watch: {
  '$route'(){
   const key = this.storageKey();
   if(key !== this.loadedKey){
    this.loadMessages();
   }
  }
 },
 mounted() {
  this.loadMessages();
  this.startBubbleCycle();
 },
 beforeUnmount(){
  if(this.bubbleInterval) clearInterval(this.bubbleInterval);
 },
 methods: {
  startBubbleCycle(){
   this.bubbleInterval = setInterval(() => {
    if(!this.isOpen){
     this.showBubble = true;
     setTimeout(() => { this.showBubble = false; }, 2500);
    }
   }, 6000);
  },
  storageKey(){
   const user = authUser();
   return user ? `chat_messages_${user.id}` : null;
  },
  loadMessages(){
   const key = this.storageKey();
   this.loadedKey = key;
   if(key){
    const saved = localStorage.getItem(key);
    if(saved){
     try{
      this.messages = JSON.parse(saved);
      return;
     }catch(e){}
    }
   }
   this.messages = [{ from: 'bot', text: 'Welcome to Algo AI, how can I help you today?' }];
  },
  saveMessages(){
   const key = this.storageKey();
   if(key){
    localStorage.setItem(key, JSON.stringify(this.messages));
   }
  },
  toggleChat(){
   this.isOpen = !this.isOpen;
   this.showBubble = false;
   if(this.isOpen){
    this.$nextTick(() => this.scrollToBottom());
   }
  },
  async sendMessage(){
   const text = this.userInput.trim();
   if(!text || this.sending) return;

   this.messages.push({ from: 'user', text });
   this.saveMessages();
   this.userInput = '';
   this.sending = true;
   this.$nextTick(() => this.scrollToBottom());

   try{
    const response = await axios.get('/chat', { params: { message: text } });
    const answerText = response.data.data?.answer || response.data.message;
    this.messages.push({ from: 'bot', text: answerText });
   }catch(error){
    this.messages.push({ from: 'bot', text: error.message || "Sorry, I couldn't find the exact answer to this. Please contact the support team for assistance." });
   }finally{
    this.saveMessages();
    this.sending = false;
    this.$nextTick(() => this.scrollToBottom());
   }
  },
  scrollToBottom(){
   const el = this.$refs.chatBody;
   if(el){
    el.scrollTop = el.scrollHeight;
   }
  }
 }
};
</script>

<template>
<div v-if="showWidget" class="agent-chat-widget">

 <!-- Floating Button -->
 <div v-if="!isOpen" class="agent-chat-fab-wrapper">

  <transition name="bubble-pop">
   <div v-if="showBubble" class="agent-hello-bubble">Hi! 👋</div>
  </transition>

  <button class="agent-chat-fab" @click="toggleChat">
   <svg class="agent-robot-icon" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    <!-- antenna -->
    <line x1="32" y1="8" x2="32" y2="14" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/>
    <circle cx="32" cy="6" r="3" fill="#fff"/>
    <!-- head -->
    <rect x="16" y="14" width="32" height="24" rx="9" fill="#fff"/>
    <circle cx="25" cy="26" r="3.2" fill="#6366f1"/>
    <circle cx="39" cy="26" r="3.2" fill="#6366f1"/>
    <path d="M25 32 Q32 36 39 32" stroke="#6366f1" stroke-width="2.2" fill="none" stroke-linecap="round"/>
    <!-- body -->
    <rect x="20" y="40" width="24" height="16" rx="6" fill="#fff" opacity="0.95"/>
    <!-- left arm (static) -->
    <line x1="20" y1="46" x2="12" y2="50" stroke="#fff" stroke-width="4" stroke-linecap="round"/>
    <!-- right arm (waving) -->
    <g class="agent-robot-wave-arm">
     <line x1="44" y1="46" x2="53" y2="38" stroke="#fff" stroke-width="4" stroke-linecap="round"/>
     <circle cx="54" cy="36" r="3.5" fill="#fff"/>
    </g>
   </svg>
  </button>

 </div>

 <!-- Chat Panel -->
 <div v-if="isOpen" class="agent-chat-panel">
  <div class="agent-chat-header">
   <span><i class="ti ti-headset me-2"></i>Algo AI Support</span>
   <i class="ti ti-x" style="cursor:pointer;" @click="toggleChat"></i>
  </div>

  <div class="agent-chat-body" ref="chatBody">
   <div v-for="(msg, index) in messages" :key="index"
    :class="['agent-chat-bubble', msg.from === 'user' ? 'agent-chat-bubble-user' : 'agent-chat-bubble-bot']">
    {{ msg.text }}
   </div>
   <div v-if="sending" class="agent-chat-bubble agent-chat-bubble-bot">
    <i class="ti ti-dots"></i> typing...
   </div>
  </div>

  <form class="agent-chat-footer" @submit.prevent="sendMessage">
   <input type="text" v-model="userInput" placeholder="Type your question..." class="form-control" :disabled="sending" />
   <button type="submit" class="btn btn-primary ms-2" :disabled="sending"><i class="ti ti-send"></i></button>
  </form>
 </div>

</div>
</template>

<style scoped>
.agent-chat-widget {
 position: fixed;
 bottom: 24px;
 right: 24px;
 z-index: 1050;
}

.agent-chat-fab-wrapper {
 position: relative;
 display: flex;
 flex-direction: column;
 align-items: flex-end;
}

.agent-hello-bubble {
 background: #fff;
 color: #1e293b;
 font-size: 13px;
 font-weight: 600;
 padding: 8px 14px;
 border-radius: 16px;
 box-shadow: 0 4px 14px rgba(0,0,0,0.18);
 margin-bottom: 10px;
 position: relative;
}

.agent-hello-bubble::after {
 content: '';
 position: absolute;
 bottom: -6px;
 right: 26px;
 width: 12px;
 height: 12px;
 background: #fff;
 transform: rotate(45deg);
 box-shadow: 3px 3px 4px rgba(0,0,0,0.06);
}

.bubble-pop-enter-active {
 animation: bubble-in 0.35s ease;
}
.bubble-pop-leave-active {
 animation: bubble-in 0.25s ease reverse;
}
@keyframes bubble-in {
 0%   { opacity: 0; transform: translateY(6px) scale(0.85); }
 100% { opacity: 1; transform: translateY(0) scale(1); }
}

.agent-chat-fab {
 display: flex;
 align-items: center;
 justify-content: center;
 width: 72px;
 height: 72px;
 border-radius: 50%;
 background: linear-gradient(135deg, #6366f1, #8b5cf6);
 border: none;
 box-shadow: 0 6px 18px rgba(99, 102, 241, 0.4);
 cursor: pointer;
 transition: box-shadow 0.15s ease;
 animation: agent-bob 3s ease-in-out infinite;
}

.agent-chat-fab:hover {
 box-shadow: 0 8px 22px rgba(99, 102, 241, 0.5);
}

@keyframes agent-bob {
 0%   { transform: translateY(0px); }
 50%  { transform: translateY(-8px); }
 100% { transform: translateY(0px); }
}

.agent-robot-icon {
 width: 48px;
 height: 48px;
 flex-shrink: 0;
}

.agent-robot-wave-arm {
 transform-origin: 44px 46px;
 animation: agent-wave 1.4s ease-in-out infinite;
}

@keyframes agent-wave {
 0%   { transform: rotate(0deg); }
 20%  { transform: rotate(-18deg); }
 40%  { transform: rotate(0deg); }
 60%  { transform: rotate(-18deg); }
 80%  { transform: rotate(0deg); }
 100% { transform: rotate(0deg); }
}

.agent-chat-panel {
 width: 340px;
 height: 460px;
 background: #fff;
 border-radius: 12px;
 box-shadow: 0 8px 30px rgba(0,0,0,0.25);
 display: flex;
 flex-direction: column;
 overflow: hidden;
}

.agent-chat-header {
 background: linear-gradient(135deg, #6366f1, #8b5cf6);
 color: #fff;
 padding: 12px 16px;
 display: flex;
 align-items: center;
 justify-content: space-between;
 font-weight: 600;
 font-size: 14px;
}

.agent-chat-body {
 flex: 1;
 padding: 12px;
 overflow-y: auto;
 background: #f8f9fb;
 display: flex;
 flex-direction: column;
 gap: 8px;
}

.agent-chat-bubble {
 max-width: 80%;
 padding: 8px 12px;
 border-radius: 10px;
 font-size: 13px;
 line-height: 1.4;
}

.agent-chat-bubble-bot {
 align-self: flex-start;
 background: #eef2ff;
 color: #1e293b;
}

.agent-chat-bubble-user {
 align-self: flex-end;
 background: #6366f1;
 color: #fff;
}

.agent-chat-footer {
 display: flex;
 padding: 10px;
 border-top: 1px solid #e2e8f0;
 background: #fff;
}
</style>